/**
 * Merchant Audit Log — "who paused my menu yesterday?"
 *
 * Lists every mutation that touched a shop the user owns: pauses, status
 * flips, menu CRUD, inventory toggles, etc. Tenant admins see the full
 * tenant-wide stream; restaurant owners see only their own shops.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCcw, ScrollText, Filter } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const ACTION_LABELS = {
    SHOP_UPDATE: { label: "Shop edited", tone: "bg-sky-100 text-sky-700" },
    SHOP_TOGGLE: { label: "Shop toggled", tone: "bg-amber-100 text-amber-700" },
    SHOP_QUICK_PAUSE: { label: "Quick pause", tone: "bg-rose-100 text-rose-700" },
    ORDER_STATUS: { label: "Order status", tone: "bg-indigo-100 text-indigo-700" },
    PRODUCT_CREATE: { label: "Menu added", tone: "bg-emerald-100 text-emerald-700" },
    PRODUCT_UPDATE: { label: "Menu edited", tone: "bg-sky-100 text-sky-700" },
    PRODUCT_DELETE: { label: "Menu deleted", tone: "bg-rose-100 text-rose-700" },
    INVENTORY_SET: { label: "Stock set", tone: "bg-amber-100 text-amber-700" },
    INVENTORY_TOGGLE: { label: "Item paused", tone: "bg-amber-100 text-amber-700" },
};

const FILTERS = [
    ["", "All actions"],
    ["SHOP_", "Shop"],
    ["PRODUCT_", "Menu"],
    ["INVENTORY_", "Inventory"],
    ["ORDER_", "Orders"],
];

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
}

function describeMeta(action, meta) {
    if (!meta) return "";
    if (action === "ORDER_STATUS") return `${meta.from || "—"} → ${meta.to || "—"}`;
    if (action === "INVENTORY_SET") return `${meta.previous ?? "—"} → ${meta.new ?? "—"} (${meta.reason || "MANUAL"})`;
    if (action === "INVENTORY_TOGGLE") return `${meta.status || ""}`;
    if (action === "SHOP_QUICK_PAUSE") return `${meta.duration || ""} · ${meta.manual_closed ? "paused" : "resumed"}`;
    if (action === "SHOP_TOGGLE") return meta.manual_closed ? "paused" : "resumed";
    if (action === "SHOP_UPDATE" || action === "PRODUCT_UPDATE") {
        const fields = (meta.fields || []).slice(0, 4).join(", ");
        const extra = (meta.fields || []).length > 4 ? "…" : "";
        return fields ? `fields: ${fields}${extra}` : "";
    }
    return "";
}

export default function MerchantAuditLog() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionFilter, setActionFilter] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: "200" });
            if (actionFilter) params.set("action", actionFilter);
            const { data } = await api.get(`/merchant/audit-log?${params.toString()}`);
            setRows(data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load audit log");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [actionFilter]);

    const grouped = useMemo(() => {
        const out = {};
        for (const r of rows) {
            const key = (r.ts || "").slice(0, 10) || "—";
            (out[key] = out[key] || []).push(r);
        }
        return Object.entries(out).sort(([a], [b]) => b.localeCompare(a));
    }, [rows]);

    return (
        <div className="space-y-5" data-testid="merchant-audit-log">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h1 className="text-xl font-bold flex items-center gap-2"><ScrollText size={20} /> Audit log</h1>
                    <p className="text-xs text-muted-foreground mt-0.5">Every change to your shops, menu, inventory and orders — newest first.</p>
                </div>
                <button
                    onClick={load}
                    data-testid="merchant-audit-refresh"
                    className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg bg-card border border-border hover:bg-muted"
                >
                    <RefreshCcw size={13} /> Refresh
                </button>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold">
                <Filter size={12} className="text-muted-foreground" />
                {FILTERS.map(([k, label]) => (
                    <button
                        key={k || "all"}
                        data-testid={`merchant-audit-filter-${k || "all"}`}
                        onClick={() => setActionFilter(k)}
                        className={`px-2.5 py-1 rounded-full border ${actionFilter === k ? "bg-foreground text-background border-foreground" : "bg-card border-border text-muted-foreground"}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {loading && (
                <p className="text-center text-sm text-muted-foreground py-10">
                    <Loader2 className="inline animate-spin mr-2" size={14} /> Loading…
                </p>
            )}

            {!loading && rows.length === 0 && (
                <div className="text-center py-16 bg-card border border-dashed border-border rounded-2xl">
                    <ScrollText className="inline mb-2 text-muted-foreground" size={22} />
                    <p className="text-sm font-bold">No activity yet</p>
                    <p className="text-xs text-muted-foreground mt-1">Edits to your shops, menu or orders will show up here.</p>
                </div>
            )}

            {!loading && grouped.map(([day, list]) => (
                <div key={day}>
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">
                        {day === "—" ? "—" : new Date(day).toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long" })}
                    </p>
                    <div className="space-y-1.5">
                        {list.map((r, i) => {
                            const meta = ACTION_LABELS[r.action] || { label: r.action, tone: "bg-muted text-muted-foreground" };
                            return (
                                <div
                                    key={`${r.ts}-${i}`}
                                    data-testid={`merchant-audit-row-${i}`}
                                    className="bg-card border border-border rounded-xl px-3 py-2 flex items-start gap-3"
                                >
                                    <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${meta.tone}`}>
                                        {meta.label}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold truncate">
                                            {r.target_label || r.target_id || "—"}
                                            <span className="ml-2 text-[11px] font-normal text-muted-foreground">{describeMeta(r.action, r.meta)}</span>
                                        </p>
                                        <p className="text-[10px] text-muted-foreground mt-0.5">
                                            <span className="font-semibold">{r.actor_name || "—"}</span>
                                            <span className="opacity-60"> · {r.actor_role || ""}</span>
                                            <span className="opacity-60"> · {fmtDate(r.ts)}</span>
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}
